import {
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	Divider,
	FormControlLabel,
	Grid,
	Stack,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import {
	AwardType,
	Condition,
	CountUnit,
	DialogAssignPage,
	OnoffType,
	RegularLoopType,
} from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import RegularDayInfoForm from './components/SingleInfoForm';
import {
	useDeleteActivitySingleMutation,
	useGetActivitySingleDetailQuery,
} from '@/api/task/activityApiSlice';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB, TASK_LIST_PAGE } from '@/common/constants/common';
import WarningIcon from '@mui/icons-material/Warning';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function ActivitySingleDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteActivitySingle] = useDeleteActivitySingleMutation();
	const { activitySingleId, activityId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');

	const countUnitList = [
		{ id: CountUnit.Time, name: t('taskManage.Time') },
		{ id: CountUnit.Minute, name: t('taskManage.Minute') },
		{ id: CountUnit.Diff, name: t('taskManage.Diff') },
		{ id: CountUnit.Day, name: t('taskManage.Day') },
	];

	const cycleTypeList = [
		{ id: RegularLoopType.Day, name: t('regularTask.day') },
		{ id: RegularLoopType.Week, name: t('regularTask.week') },
		{ id: RegularLoopType.Month, name: t('regularTask.month') },
	];

	const conditionList = [
		{ id: Condition.OpenApp, name: t('taskManage.OpenApp') },
		// { id: Condition.Online, name: t('taskManage.Online') },
		{ id: Condition.Topage, name: t('taskManage.Topage') },
		{ id: Condition.Exercise, name: t('taskManage.Exercise') },
		{ id: Condition.NewExercise, name: t('taskManage.NewExercise') },
		{ id: Condition.Health, name: t('taskManage.Health') },
		{ id: Condition.ExerciseClock, name: t('taskManage.ExerciseClock') },
		{ id: Condition.MajorExercise, name: t('taskManage.MajorExercise') },
		{ id: Condition.HeartExercise, name: t('taskManage.HeartExercise') },
		{
			id: Condition.CrossoverExercise,
			name: t('taskManage.CrossoverExercise'),
		},
		{ id: Condition.BodyExercise, name: t('taskManage.BodyExercise') },
		{
			id: Condition.MeditationExercise,
			name: t('taskManage.MeditationExercise'),
		},
		{ id: Condition.Day21Exercise, name: t('taskManage.Day21Exercise') },
		{ id: Condition.AllExercise, name: t('taskManage.AllExercise') },
		{ id: Condition.ChangeEmail, name: t('taskManage.ChangeEmail') },
		{ id: Condition.ChangePhone, name: t('taskManage.ChangePhone') },
		{ id: Condition.ChangeNickName, name: t('taskManage.ChangeNickName') },
		{ id: Condition.ChangeProfile, name: t('taskManage.ChangeProfile') },
		{
			id: Condition.AssignAllExercise,
			name: t('taskManage.AssignAllExercise'),
		},
	];
	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];

	const { data: details } = useGetActivitySingleDetailQuery(
		activitySingleId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.published == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteTask'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('taskManage.deleteActivitySingle')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('taskManage.isDeleteActivitySingle')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteActivitySingle(activitySingleId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSingleSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(`/task-manage/activity-list/info/${activityId}`, {
							replace: true,
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.activityTaskDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('taskManage.activityStatus')}
									valueElement={
										<Box display='flex' alignItems='center'>
											<PublishedChip
												published={details?.data.published}
												publishList={PUBLISHED_LISTB}
											/>
											{details?.data.published == OnoffType.on &&
												details?.data.allRewardUnpublished && (
													<Tooltip
														title={`${t('regularTask.statusTips')}`}
														sx={{ color: '#FA9E26' }}
													>
														<WarningIcon />
													</Tooltip>
												)}
										</Box>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('taskManage.taskName')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('taskManage.taskDescription')}
													value={
														currentTab == 'zh'
															? details?.data.descriptionZh
															: currentTab == 'en'
															? details?.data.descriptionEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.pushedTime')}
													value={
														!details?.data.publishedStartAt
															? '-'
															: dayjs(details?.data.publishedStartAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.pulledTime')}
													value={
														!details?.data.publishedEndAt
															? '-'
															: dayjs(details?.data.publishedEndAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
											<Grid item xs={6}>
												{details?.data && (
													<FormControlLabel
														control={
															<Checkbox
																disabled
																checked={details?.data.isCycle ? true : false}
															/>
														}
														label={t('taskManage.loopTask')}
													/>
												)}
											</Grid>
											{details?.data.isCycle && (
												<Grid item xs={6}>
													<AppFieldView
														label={t('taskManage.loopType')}
														value={showLabel(
															details?.data.cycleType,
															cycleTypeList
														)}
													/>
												</Grid>
											)}
										</Grid>
										<Divider />
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.completionConditionsMore')}
													value={showLabel(
														details?.data.finishedCondition,
														conditionList
													)}
												/>
											</Grid>
											{details?.data.finishedCondition == Condition.Topage && (
												<Grid item xs={6}>
													<AppFieldView
														label={t('dialogList.showPage')}
														value={showLabel(
															details?.data.pageIndex,
															TASK_LIST_PAGE
														)}
													/>
												</Grid>
											)}
											{details?.data.finishedCondition ==
												Condition.Exercise && (
												<Grid item xs={6}>
													<AppFieldView
														label={t('taskManage.designatedExercise')}
														value={details?.data.exerciseName}
													/>
												</Grid>
											)}
											{details?.data.finishedCondition ==
												Condition.AssignAllExercise && (
												<Grid item xs={6}>
													<AppFieldView
														label={t('taskManage.designatedSeries')}
														value={details?.data.exerciseSeriesName}
													/>
												</Grid>
											)}

											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.quantityUnit')}
													value={showLabel(
														details?.data.finishedConditionUnit,
														countUnitList
													)}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.total')}
													value={details?.data.finishedNum}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.awardType')}
													value={showLabel(
														details?.data.rewardType,
														awardTypeList
													)}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.rewardPoints')}
													value={details?.data.rewardPoints}
												/>
											</Grid>
										</Grid>
										<Grid container marginBottom={3}>
											{details?.data.rewardList?.map((item) => (
												<>
													<Grid item xs={1}>
														<AppFieldView
															valueElement={
																<PublishedChip
																	published={item.published}
																	publishList={PUBLISHED_LISTB}
																/>
															}
														/>
													</Grid>
													<Grid item xs={1}>
														<AppFieldView
															label={t('regularTask.rewardItem')}
															value={item.rewardDesc}
														/>
													</Grid>
													<Grid item xs={10}>
														<AppFieldView
															label={t('regularTask.rewardItemCount')}
															value={item.rewardNum}
														/>
													</Grid>
												</>
											))}
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
										</Box>
										<RegularDayInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
